"use client";

import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import {
  PURCHASE_REQUEST_STATUS_COLORS,
  purchaseRequestStatusLabel,
} from "@/constants/purchase-request";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import type {
  ApprovalAssigneeSummary,
  PurchaseRequestApprovalSummary,
} from "@/types";
import { formatDateAR } from "@/utils/date";

export interface ApprovalsRow {
  id: number;
  request_number: string;
  status: string;
  created_at: string;
  sector?: { id: number; name: string };
  approval_summary?: PurchaseRequestApprovalSummary;
  assigned_to_current_user?: boolean;
}

function formatAssignees(list: ApprovalAssigneeSummary[] | undefined) {
  if (!list || list.length === 0) return "-";
  const names = list.map((a) => a.full_name?.trim() ?? a.email).filter(Boolean);
  return names.join(", ");
}

export const columns: ColumnDef<ApprovalsRow>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "sector.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sector" />
    ),
    cell: ({ row }) => row.original.sector?.name ?? "-",
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      const color =
        PURCHASE_REQUEST_STATUS_COLORS[
          status as keyof typeof PURCHASE_REQUEST_STATUS_COLORS
        ] ?? "";
      return (
        <Badge className={color}>{purchaseRequestStatusLabel(status)}</Badge>
      );
    },
  },
  {
    id: "current_approvers",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Pendiente por" />
    ),
    cell: ({ row }) => {
      const summary = row.original.approval_summary;
      const names = formatAssignees(summary?.assignees);
      return <span>{names}</span>;
    },
    enableSorting: false,
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Creado" />
    ),
    cell: ({ row }) => <span>{formatDateAR(row.original.created_at)}</span>,
  },
];
